import {
  createSlice,
  createAsyncThunk,
  createSelector,
  PayloadAction,
} from "@reduxjs/toolkit";
import { toast } from "sonner";
import { AxiosError } from "axios";
import {
  Schedule,
  ScheduleState,
  AttendanceRecord,
  ScheduleQueryParams,
  SessionStatus,
} from "./scheduleTypes";
import apiService from "../../services/api.services";

// Constants
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Helper functions
const createCacheKey = (params: ScheduleQueryParams): string => {
  return Object.entries(params)
    .filter(([_, value]) => value !== undefined)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}:${value}`)
    .join("|");
};

// Initial state
const initialState: ScheduleState = {
  schedules: {},
  attendanceRecords: {},
  status: "idle",
  error: null,
  total: 0,
  currentPage: 1,
  lastFetched: {},
  activeRequests: {},
  currentSchedule: null,
};

// Thunks
export const fetchSchedules = createAsyncThunk<
  { schedules: Schedule[]; pagination: any; cacheKey: string } | null,
  ScheduleQueryParams,
  { state: { schedules: ScheduleState }; rejectValue: { message: string } }
>(
  "schedules/fetchSchedules",
  async (params, { getState, rejectWithValue, dispatch }) => {
    const state = getState();
    const cacheKey = createCacheKey(params);
    const requestId = `fetch_${cacheKey}`;

    if (state.schedules.activeRequests[requestId]) {
      return null;
    }

    const lastFetched = state.schedules.lastFetched[cacheKey] || 0;
    if (Date.now() - lastFetched < CACHE_DURATION) {
      return null;
    }

    try {
      dispatch(setActiveRequest(requestId));
      const response = await apiService.get<{
        schedule: Schedule[];
        pagination: any;
      }>(`/schedule`, {
        params,
      });
      return {
        schedules: response.schedule,
        pagination: response.pagination,
        cacheKey,
      };
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      toast.error(
        axiosError.response?.data?.message || "Failed to fetch schedules"
      );
      return rejectWithValue({
        message:
          axiosError.response?.data?.message || "Failed to fetch schedules",
      });
    } finally {
      dispatch(removeActiveRequest(requestId));
    }
  }
);

export const createSchedule = createAsyncThunk<
  Schedule,
  { formData: FormData },
  { rejectValue: { message: string } }
>("schedules/createSchedule", async ({ formData }, { rejectWithValue }) => {
  try {
    const response = await apiService.post<Schedule>(
      `/schedule/create`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    toast.success("Schedule created successfully");
    return response;
  } catch (error) {
    const axiosError = error as AxiosError<{ message: string; error: string }>;
    toast.error(
      axiosError.response?.data?.error || "Failed to create schedule"
    );
    return rejectWithValue({
      message: axiosError.response?.data?.error || "Failed to create schedule",
    });
  }
});

export const updateSchedule = createAsyncThunk<
  Schedule,
  { id: number; updates: Partial<Schedule> },
  { rejectValue: { message: string } }
>("schedules/updateSchedule", async ({ id, updates }, { rejectWithValue }) => {
  try {
    const response = await apiService.put<Schedule>(
      `/schedules/${id}`,
      updates
    );
    toast.success("Schedule updated successfully");
    return response;
  } catch (error) {
    const axiosError = error as AxiosError<{ message: string }>;
    toast.error(
      axiosError.response?.data?.message || "Failed to update schedule"
    );
    return rejectWithValue({
      message:
        axiosError.response?.data?.message || "Failed to update schedule",
    });
  }
});

export const cancelSchedule = createAsyncThunk<
  number,
  number,
  { rejectValue: { message: string } }
>("schedules/cancelSchedule", async (id, { rejectWithValue }) => {
  try {
    await apiService.post(`/schedule/${id}/cancel`, {});
    toast.success("Schedule cancelled successfully");
    return id;
  } catch (error) {
    const axiosError = error as AxiosError<{ message: string }>;
    toast.error(
      axiosError.response?.data?.message || "Failed to cancel schedule"
    );
    return rejectWithValue({
      message:
        axiosError.response?.data?.message || "Failed to cancel schedule",
    });
  }
});

export const deleteSchedule = createAsyncThunk<
  number,
  number,
  { rejectValue: { message: string } }
>("schedules/deleteSchedule", async (id, { rejectWithValue }) => {
  try {
    await apiService.delete(`/schedule/${id}/delete`);
    toast.success("Schedule deleted successfully");
    return id;
  } catch (error) {
    const axiosError = error as AxiosError<{ message: string }>;
    toast.error(
      axiosError.response?.data?.message || "Failed to delete schedule"
    );
    return rejectWithValue({
      message:
        axiosError.response?.data?.message || "Failed to delete schedule",
    });
  }
});

export const recordAttendance = createAsyncThunk<
  { scheduleId: number; attendance: AttendanceRecord },
  { scheduleId: number; attendanceStatus: "Present" | "Absent" | "Late" },
  { rejectValue: { message: string } }
>(
  "schedules/recordAttendance",
  async ({ scheduleId, attendanceStatus }, { rejectWithValue }) => {
    try {
      const response = await apiService.post<{ data: AttendanceRecord }>(
        `/schedules/${scheduleId}/attendance`,
        { attendanceStatus }
      );
      return { scheduleId, attendance: response.data };
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      toast.error(
        axiosError.response?.data?.message || "Failed to record attendance"
      );
      return rejectWithValue({
        message:
          axiosError.response?.data?.message || "Failed to record attendance",
      });
    }
  }
);

// Slice
const scheduleSlice = createSlice({
  name: "schedules",
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setCurrentSchedule: (state, action: PayloadAction<Schedule | null>) => {
      state.currentSchedule = action.payload;
    },
    resetScheduleError: (state) => {
      state.error = null;
    },
    resetScheduleStatus: (state) => {
      state.status = "idle";
    },
    invalidateCache: (state) => {
      state.lastFetched = {};
    },
    setActiveRequest: (state, action: PayloadAction<string>) => {
      state.activeRequests[action.payload] = true;
    },
    removeActiveRequest: (state, action: PayloadAction<string>) => {
      delete state.activeRequests[action.payload];
    },
    setScheduleStatus: (state, action: PayloadAction<any>) => {
      const status = action.payload;
      state.status = status;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch schedules cases
      .addCase(fetchSchedules.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchSchedules.fulfilled, (state, action) => {
        if (action.payload) {
          const { schedules, pagination, cacheKey } = action.payload;
          state.schedules = schedules.reduce(
            (acc, schedule) => ({
              ...acc,
              [schedule.id]: schedule,
            }),
            {}
          );
          state.total = pagination.total;
          state.currentPage = pagination.page;
          state.lastFetched[cacheKey] = Date.now();
          state.status = "succeeded";
        }
      })
      .addCase(fetchSchedules.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Failed to fetch schedules";
        state.status = "idle";
      })

      // Create schedule cases
      .addCase(createSchedule.pending, (state, action) => {
        state.error = null;
        state.status = "loading";
      })
      .addCase(createSchedule.fulfilled, (state, action) => {
        state.schedules[action.payload.id] = action.payload;
        state.status = "succeeded";
      })

      .addCase(createSchedule.rejected, (state, action) => {
        state.error = action.payload?.message || "Failed to create schedules";
        state.status = "failed";
      })

      // Update schedule cases
      .addCase(updateSchedule.fulfilled, (state, action) => {
        state.schedules[action.payload.id] = action.payload;
      })

      // Cancel schedule cases
      .addCase(cancelSchedule.fulfilled, (state, action) => {
        if (state.schedules[action.payload]) {
          state.schedules[action.payload].status = "Cancelled";
        }
      })

      // Delete schedule cases
      .addCase(deleteSchedule.fulfilled, (state, action) => {
        delete state.schedules[action.payload];
      })
      .addCase(recordAttendance.fulfilled, (state, action) => {
        const { scheduleId, attendance } = action.payload;
        if (!state.attendanceRecords[scheduleId]) {
          state.attendanceRecords[scheduleId] = [];
        }
        state.attendanceRecords[scheduleId].push(attendance);
      });
  },
});

// Actions
export const {
  setCurrentPage,
  setCurrentSchedule,
  resetScheduleError,
  invalidateCache,
  setActiveRequest,
  removeActiveRequest,
  setScheduleStatus,
  resetScheduleStatus,
} = scheduleSlice.actions;

// Selectors
export const selectSchedules = createSelector(
  [(state: { schedules: ScheduleState }) => state.schedules.schedules],
  (schedules): Schedule[] => Object.values(schedules)
);

export const selectScheduleById = createSelector(
  [
    (state: { schedules: ScheduleState }) => state.schedules.schedules,
    (_: any, id: number) => id,
  ],
  (schedules, id): Schedule | undefined => schedules[id]
);

export const selectScheduleAttendance = createSelector(
  [
    (state: { schedules: ScheduleState }) => state.schedules.attendanceRecords,
    (_: any, scheduleId: number) => scheduleId,
  ],
  (attendanceRecords, scheduleId): AttendanceRecord[] =>
    attendanceRecords[scheduleId] || []
);

export const selectCurrentSchedule = createSelector(
  [(state: { schedules: ScheduleState }) => state.schedules.currentSchedule],
  (currentSchedule) => currentSchedule
);

// Helper function to combine date and time strings into a Date object
const combineDateTime = (dateStr: string, timeStr: string): Date => {
  const scheduleDate = new Date(dateStr);
  const [hours, minutes] = timeStr.split(":").map(Number);

  return new Date(
    scheduleDate.getFullYear(),
    scheduleDate.getMonth(),
    scheduleDate.getDate(),
    hours,
    minutes
  );
};

// Selector to get the next upcoming schedule
export const selectNextSchedule = createSelector(
  [(state: { schedules: ScheduleState }) => state.schedules.schedules],
  (schedules): Schedule | null => {
    const now = new Date();
    const upcoming = Object.values(schedules)
      // Filter out cancelled schedules
      .filter(
        (schedule) =>
          schedule.status !== "Cancelled" && schedule.status !== "Completed"
      )
      // Convert schedule times to comparable Date objects
      .map((schedule) => ({
        ...schedule,
        compareTime: combineDateTime(
          schedule.scheduledDate,
          schedule.startTime
        ),
      }))
      // Filter out past schedules
      .filter((schedule) => schedule.compareTime > now)
      // Sort by closest upcoming time
      .sort((a, b) => a.compareTime.getTime() - b.compareTime.getTime());

    // Return the first (closest) upcoming schedule, or null if none found
    if (upcoming.length > 0) {
      const { compareTime, ...nextSchedule } = upcoming[0];
      return nextSchedule;
    }
    return null;
  }
);

export const selectTodaySchedules = createSelector(
  [(state: { schedules: ScheduleState }) => state.schedules.schedules],
  (schedules): Schedule[] => {
    // Get today's date in YYYY-MM-DD format
    const today = new Date().toISOString().split("T")[0];

    // Filter schedules for today
    return (
      Object.values(schedules)
        .filter(
          (schedule) =>
            // Check if the scheduled date matches today's date
            schedule.scheduledDate === today &&
            // Optionally, filter out cancelled schedules
            schedule.status !== "Cancelled"
        )
        // Optionally, sort by start time
        .sort((a, b) => {
          const [aHours, aMinutes] = a.startTime.split(":").map(Number);
          const [bHours, bMinutes] = b.startTime.split(":").map(Number);
          return aHours * 60 + aMinutes - (bHours * 60 + bMinutes);
        })
    );
  }
);

export default scheduleSlice.reducer;
